// KidCookBanner — sits at the top of the Pet tab whenever a family
// member has an active cook session. Tap to jump to the Watch tab.
//
// Without this banner the kid had no surface-level signal that
// someone in the family was cooking — they had to know to navigate
// to the Watch tab and check. The banner surfaces "🍳 Mom is cooking
// Boerenkool Stamppot" with a CTA so they're nudged toward the
// live cook view.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT } from "../../lib/tokens";

export default function KidCookBanner({ userId, onTapToWatch }) {
  const [session, setSession] = useState(null);
  const [leadName, setLeadName] = useState(null);

  // Same family-cook discovery as KidWatchTab. Could be extracted
  // into a shared hook later; today both subscribers re-fetch from
  // the same realtime channel, which is fine for this scale.
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    const findActive = async () => {
      const { data: rels } = await supabase
        .from("relationships")
        .select("requester_id, addressee_id")
        .eq("kind", "family")
        .eq("status", "accepted")
        .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
      if (cancelled) return;
      const familyIds = (rels || [])
        .map((r) => (r.requester_id === userId ? r.addressee_id : r.requester_id))
        .filter(Boolean);
      if (familyIds.length === 0) {
        setSession(null);
        return;
      }
      const { data: rows } = await supabase
        .from("cook_sessions")
        .select("id, user_id, recipe_slug, recipe_title, recipe_emoji, status, started_at")
        .in("user_id", familyIds)
        .eq("status", "active")
        .order("started_at", { ascending: false });
      if (cancelled) return;
      setSession((rows || [])[0] || null);
    };
    findActive();

    const ch = supabase
      .channel(`rt:kidcookbanner:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_sessions" },
        () => findActive(),
      )
      .subscribe();
    return () => {
      cancelled = true;
      try { supabase.removeChannel(ch); } catch {}
    };
  }, [userId]);

  // Lookup the lead's display name for "Mom is cooking" framing.
  useEffect(() => {
    if (!session?.user_id) {
      setLeadName(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("name, username")
        .eq("id", session.user_id)
        .maybeSingle();
      if (cancelled) return;
      setLeadName(data?.name || data?.username || "Someone");
    })();
    return () => { cancelled = true; };
  }, [session?.user_id]);

  if (!session) return null;

  return (
    <button
      type="button"
      onClick={onTapToWatch}
      style={{
        width: "100%",
        maxWidth: 420,
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        marginBottom: 12,
        background: `linear-gradient(135deg, ${COLOR.leafDeep} 0%, rgba(47,143,131,0.22) 100%)`,
        border: `1.5px solid ${COLOR.leaf}`,
        borderRadius: 14,
        cursor: "pointer",
        textAlign: "left",
        touchAction: "manipulation",
        boxShadow: `0 4px 16px ${COLOR.leaf}33`,
        animation: "kid-cook-banner-pulse 2.6s ease-in-out infinite",
      }}
    >
      <style>{`
        @keyframes kid-cook-banner-pulse {
          0%, 100% { box-shadow: 0 4px 16px ${COLOR.leaf}33; }
          50%      { box-shadow: 0 4px 20px ${COLOR.leaf}66; }
        }
      `}</style>
      <span style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>
        {session.recipe_emoji || "🍳"}
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
        <span style={{
          fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
          letterSpacing: "0.14em", color: COLOR.leaf,
        }}>
          {(leadName || "SOMEONE").toUpperCase()} IS COOKING
        </span>
        <span style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic",
          fontSize: 18, color: COLOR.ink, lineHeight: 1.2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {session.recipe_title || "a recipe"}
        </span>
      </span>
      <span style={{
        flexShrink: 0,
        padding: "6px 12px",
        background: COLOR.leaf,
        color: "#fff",
        borderRadius: 999,
        fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
        letterSpacing: "0.1em",
      }}>
        WATCH →
      </span>
    </button>
  );
}
